<template>
  <div class="input-screen-page-wrapper" style="width: 100%; height: 100%">
    <!-- Menu options -->

    <inputs-grid
      ref="inputsGrid"
      :additional-columns="additionalColumns"
      :column-validations="columnValidations"
      :additional-grid-options="additionalGridOptions"
      :save-changes="saveChanges"
      :export-action="downloadEngineInputs"
      :is-exporting="downloading"
      route-suffix="engine"
    >
      <!-- Slots for inputs-grid, -->
      <!-- Import/ Add buttons etc. -->
      <template #buttons>
        <feature-toggle v-if="!isSelectedWorkpackageMaster" :toggle="enableMarginInitialisation">
          <div class="workflow-controls">
            <div class="d-flex workflow-buttons">
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn
                    small
                    color="primary"
                    :loading="runningMarginInitialisation"
                    depressed
                    v-on="on"
                  >
                    {{ $t('actions.initialiseMargin') }}
                    <v-icon>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="runMarginInitialisation(true)">
                    <v-list-item-title>
                      {{ $t('actions.initialiseMarginWithDelta') }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="runMarginInitialisation(false)">
                    <v-list-item-title>{{
                      $t('actions.initialiseMarginWithoutDelta')
                    }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="workflow-info-row">
              <last-triggered-info
                class="mr-0"
                :last-triggered-info="lastTriggeredInfo"
                :loading="isLastTriggeredLoading"
                :translation-time-key="'engineInputs.editor.initialisationLastRunTime'"
                :width="200"
              />
            </div>
          </div>
        </feature-toggle>
        <feature-toggle
          v-if="!isSelectedWorkpackageMaster && hasAOExtracts"
          :toggle="enableAOExtracts"
        >
          <div class="workflow-controls">
            <div class="d-flex workflow-buttons">
              <v-btn
                color="primary"
                class="pa-0"
                small
                depressed
                :loading="runningRefreshForecastVolume"
                @click="isRefreshForecastDialogOpen = true"
              >
                <v-icon small>mdi-autorenew</v-icon>
                <span class="pl-1">{{ $t('actions.refreshForecastVolume') }}</span>
              </v-btn>
            </div>
            <div class="workflow-info-row">
              <last-triggered-info
                class="mr-0"
                :last-triggered-info="lastRefreshTriggeredInfo"
                :loading="isLastRefreshTriggeredLoading"
                :translation-time-key="'engineInputs.editor.refreshForecastVolumeLastRunTime'"
                :width="200"
              />
            </div>
          </div>
        </feature-toggle>
        <engine-input-upload v-if="canEditAttributes" />
      </template>
    </inputs-grid>

    <confirm-dialog
      :is-open="isRefreshForecastDialogOpen"
      :ok-btn-text="$t('actions.yesProceed')"
      @onOk="confirmRefreshForecast"
      @onCancel="isRefreshForecastDialogOpen = false"
    >
      <template v-slot:body>
        <div>
          {{ $tkey('dialogs.refreshForecastConfirm') }}
        </div>
        <div>{{ $tkey('dialogs.areYouSureToProceed') }}</div>
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import axios from 'axios';
import to from 'await-to-js';
import moment from 'moment';
import {
  get,
  startCase,
  lowerCase,
  each,
  pick,
  isNil,
  isNaN,
  round,
  flatMap,
  isEmpty,
} from 'lodash';
import featureFlagsMixin from '../../../mixins/featureFlags';
import numberFormats from '@enums/number-formats';
import {
  useZones,
  displayHierarchyAndFilter,
  useForecastVolume,
  allowTemplateIntentionCostChange,
  allowNegativeTargetMargin,
  allowGreaterThan100PercentTargetMargin,
  displayClientProductKey,
  enableMarginInitialisation,
  enableReferenceDate,
  enableAOExtracts,
} from '@enums/feature-flags';
import { yearMonthDayFormat } from '@enums/date-formats';
import DataTypes from '@enums/data-types';
import Events from '@enums/events';
import engineInputsMixin from '../../../mixins/engineInputs';
import inputScreensTableMixin from '../../../mixins/inputScreensTable';
import { descending } from '@enums/sort-direction';
import tablesMixin from '../../../mixins/tables';
import marginInitialisationMixin from '../../../mixins/marginInitialisation';
import { inputTableCssClasses } from '@enums/tables';
import { categoryLevel, pricingGroupLevel, architectureGroupLevel } from '@enums/hierarchy';
import { maxDateTimestamp } from '@enums/dates';
import datePickerEditor from '../../../components/date-picker-editor.vue';
import {
  validations,
  percentageNumericCol,
  localisedNumericCol,
  localisedDateCol,
  getCostFormatNumericCol,
} from '../../../utils/inputs-grid-utils';

export default {
  name: 'EngineInputsEditor',

  localizationKey: 'engineInputs.editor',

  components: {
    // register vue components
    // eslint thinks that it's not used, but headerComponent points to it
    // eslint-disable-next-line vue/no-unused-components
    datePickerEditor,
  },
  mixins: [
    featureFlagsMixin,
    inputScreensTableMixin,
    tablesMixin,
    engineInputsMixin,
    marginInitialisationMixin,
  ],

  async beforeRouteLeave(destination, from, next) {
    const preventNavigation = await this.$refs.inputsGrid.resolveEditsBeforeNavigation();

    if (preventNavigation) return;

    next();
  },

  data() {
    return {
      get,
      startCase,
      additionalGridOptions: {
        getMainMenuItems: this.getColumnMenuItems,
      },
      maxMomentDate: moment.utc(maxDateTimestamp),
      dateColumnTranslations: {
        effectiveDate: this.$tkey('effectiveDate'),
        expiryDate: this.$tkey('expiryDate'),
      },
      runningRefreshForecastVolume: false,
      isLastTriggeredLoading: false,
      isLastRefreshTriggeredLoading: false,
      lastTriggeredInfo: {},
      lastRefreshTriggeredInfo: {},
      enableMarginInitialisation,
      enableAOExtracts,
      isRefreshForecastDialogOpen: false,
    };
  },

  computed: {
    ...mapState('workpackages', ['selectedWorkpackage']),
    ...mapState('engineInputs', ['downloading']),
    ...mapState('hierarchy', {
      loadingHierarchy: 'loading',
      busyImportingGroup: 'busyImportingGroup',
    }),
    ...mapState('competitorMetadata', ['competitorKeyToTypeMap']),
    ...mapGetters('competitorMetadata', ['feedCompetitorNames']),
    ...mapGetters('context', ['isPricingSpecialist', 'isOWUser']),
    ...mapGetters('workpackages', ['isSelectedWorkpackageMaster']),
    ...mapState('filters', ['retailAttributesFilter']),
    ...mapState('clientConfig', [
      'exportConfigs',
      'dateFormats',
      'i18nconfig',
      'numberFormats',
      'toggleLogic',
    ]),

    zonesEnabled() {
      return this.isFeatureFlagEnabled(useZones);
    },

    enableReferenceDate() {
      return this.isFeatureFlagEnabled(enableReferenceDate);
    },

    fixedColumnsClass() {
      return this.toolStoreGroupColumn
        ? inputTableCssClasses.threeFixedColumns
        : inputTableCssClasses.twoFixedColumns;
    },

    showHierarchy() {
      return this.isFeatureFlagEnabled(displayHierarchyAndFilter);
    },

    showForecastVolumeColumn() {
      return this.isFeatureFlagEnabled(useForecastVolume);
    },

    canEditAttributes() {
      return this.isPricingSpecialist;
    },

    futureCostEditDisabled() {
      return (
        this.isSelectedWorkpackageMaster &&
        !this.isFeatureFlagEnabled(allowTemplateIntentionCostChange)
      );
    },

    aoExtractsEnabled() {
      return !this.isSelectedWorkpackageMaster && this.isFeatureFlagEnabled(enableAOExtracts);
    },

    columnValidations() {
      // Fields must match colId
      const targetMarginValidations = ['mustBeValidNumber'];
      // Add lower bound validation unless negative margins are allowed
      if (!this.isFeatureFlagEnabled(allowNegativeTargetMargin)) {
        targetMarginValidations.push('greaterThan0');
      }
      // Add upper bound validation unless margins > 100% are allowed
      if (!this.isFeatureFlagEnabled(allowGreaterThan100PercentTargetMargin)) {
        targetMarginValidations.push('lessThanOrEqual100');
      }
      return {
        'mandatoryEngineInputs::targetMargin': pick(validations, ...targetMarginValidations),
        'mandatoryEngineInputs::promoParticipationSales': pick(
          validations,
          'mustBeValidNumber',
          'lessThanOrEqual100',
          'greaterThanOrEqual0'
        ),
        'mandatoryEngineInputs::promoFunding': pick(
          validations,
          'mustBeValidNumber',
          'lessThanOrEqual100',
          'greaterThanOrEqual0'
        ),
        'mandatoryEngineInputs::promoDiscount': pick(
          validations,
          'mustBeValidNumber',
          'lessThan100',
          'greaterThanOrEqual0'
        ),
        'mandatoryEngineInputs::forecastVolume': pick(validations, 'greaterThanOrEqual0'),
      };
    },

    exportTranslationMap() {
      const hierarchyColumnTranslations = this.showHierarchy
        ? {
            [`hierarchy.${categoryLevel}.levelEntryDescription`]: this.$t('pricing.category'),
            [`hierarchy.${pricingGroupLevel}.levelEntryDescription`]: this.$t(
              'pricing.pricingGroup'
            ),
            [`hierarchy.${architectureGroupLevel}.levelEntryDescription`]: this.$t(
              'pricing.architectureGroup'
            ),
          }
        : {};

      const toolStoreGroupColumn = this.zonesEnabled
        ? { toolStoreGroupKey: this.$t('pricing.toolStoreGroup') }
        : {};

      const clientProductKeyColumn = this.isFeatureFlagEnabled(displayClientProductKey)
        ? { clientProductKey: this.$t('pricing.clientProductKey') }
        : {};

      return {
        productKeyDisplay: this.$tkey('productKey'),
        ...clientProductKeyColumn,
        ...toolStoreGroupColumn,
        productName: this.$tkey('productDescription'),
        ...hierarchyColumnTranslations,
        ...this.getEngineInputsTranslations(),
        ...this.dateColumnTranslations,
      };
    },

    hierarchyHeaders() {
      return this.showHierarchy
        ? [
            {
              text: this.$t('pricing.category'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${categoryLevel}.levelEntryDescription`,
              class: 'm-width',
              dataType: DataTypes.str,
            },
            {
              text: this.$t('pricing.pricingGroup'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${pricingGroupLevel}.levelEntryDescription`,
              class: 'm-width',
              dataType: DataTypes.str,
            },
            {
              text: this.$t('pricing.architectureGroup'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${architectureGroupLevel}.levelEntryDescription`,
              class: 'border-right m-width',
              dataType: DataTypes.str,
            },
          ]
        : [];
    },

    toolStoreGroupColumn() {
      return this.isFeatureFlagEnabled(useZones)
        ? {
            ...this.toolStoreGroupColumnDefaultProperties,
            text: this.$tkey('toolStoreGroup'),
            class: `m-width ${inputTableCssClasses.threeFixedColumns}`,
          }
        : null;
    },

    headers() {
      const tsgHeader = this.toolStoreGroupColumn ? [this.toolStoreGroupColumn] : [];
      const forecastVolumeHeader = this.showForecastVolumeColumn
        ? [
            {
              text: this.$tkey('forecastVolume'),
              align: 'start',
              sortable: true,
              value: 'mandatoryEngineInputs.forecastVolume',
              dataType: DataTypes.number,
            },
            {
              text: this.$tkey('forecastVolumeSource'),
              align: 'start',
              sortable: true,
              value: 'mandatoryEngineInputs.forecastVolumeSource',
              dataType: DataTypes.str,
            },
          ]
        : [];
      return [
        {
          text: this.$tkey('productKey'),
          align: 'start',
          sortable: true,
          value: 'productKeyDisplay',
          class: `s-width ${this.fixedColumnsClass}`,
          dataType: DataTypes.str,
          formatter: {
            type: get(
              this.exportConfigs,
              'exportToExcel.columnFormatter.productKeyDisplay',
              numberFormats.integer
            ),
          },
        },
        ...tsgHeader,
        {
          text: this.$tkey('productDescription'),
          align: 'start',
          sortable: true,
          value: 'productName',
          class: `l-width border-right ${this.fixedColumnsClass}`,
          dataType: DataTypes.str,
        },
        ...this.hierarchyHeaders,
        ...forecastVolumeHeader,
        {
          text: this.$tkey('targetMargin'),
          align: 'start',
          sortable: true,
          value: 'mandatoryEngineInputs.targetMargin',
          unit: '%',
          dataType: DataTypes.number,
        },
        {
          text: this.$tkey('creationCost'),
          align: 'start',
          sortable: true,
          value: 'mandatoryEngineInputs.creationCost',
          unit: this.$t('clientCurrencySymbol'),
          dataType: DataTypes.number,
        },
        {
          text: this.$tkey('goLiveCost'),
          align: 'start',
          sortable: true,
          value: 'mandatoryEngineInputs.nonPromoNetCost',
          unit: this.$t('clientCurrencySymbol'),
          dataType: DataTypes.number,
        },
        {
          text: this.$tkey('promoParticipationSales'),
          align: 'start',
          sortable: true,
          value: 'mandatoryEngineInputs.promoParticipationSales',
          unit: '%',
          dataType: DataTypes.number,
        },
        {
          text: this.$tkey('promoDiscount'),
          align: 'start',
          sortable: true,
          value: 'mandatoryEngineInputs.promoDiscount',
          unit: '%',
          dataType: DataTypes.number,
        },
        {
          text: this.$tkey('promoFunding'),
          align: 'start',
          sortable: true,
          value: 'mandatoryEngineInputs.promoFunding',
          unit: '%',
          dataType: DataTypes.number,
        },
        {
          text: this.$tkey('intentionCost'),
          align: 'start',
          sortable: true,
          value: 'mandatoryEngineInputs.intentionCost.price',
          unit: this.$t('clientCurrencySymbol'),
          dataType: DataTypes.number,
          deprecated: true,
        },
      ];
    },

    getTodaysDate() {
      return moment().format(yearMonthDayFormat);
    },

    additionalColumns() {
      const forecastVolumeColumn = this.showForecastVolumeColumn
        ? [
            {
              headerName: `${this.$tkey('forecastVolume')}`,
              headerTooltip: `${this.$tkey('forecastVolume')}`,
              colId: `mandatoryEngineInputs::forecastVolume`,
              field: `mandatoryEngineInputs.forecastVolume`,
              editable: true,
              tooltipValueGetter: this.tooltipValueGetter,
              ...localisedNumericCol,
            },
            {
              headerName: `${this.$tkey('forecastVolumeSource')}`,
              headerTooltip: `${this.$tkey('forecastVolumeSource')}`,
              colId: `mandatoryEngineInputs::forecastVolumeSource`,
              field: `mandatoryEngineInputs.forecastVolumeSource`,
              editable: false,
              valueFormatter: params => startCase(lowerCase(params.value)),
              filter: 'agSetColumnFilter',
              filterParams: {
                valueFormatter: params => startCase(lowerCase(params.value)),
              },
            },
          ]
        : [];
      // show initialised margin column if feature flag is enabled
      const initialisedMarginColumn = this.marginInitialisationEnabled
        ? [
            this.createPercentageColumn('initialisedMargin', {
              editable: false,
              cellEditorParams: params => {
                if (isNaN(params.value)) return { value: params.value };
                if (isNil(params.value)) return { value: undefined };
                return { value: round(params.value, 2) };
              },
            }),
          ]
        : [];
      const initialisedMarginInfoGroup =
        this.marginInitialisationEnabled && this.isOWUser
          ? [
              {
                headerName: this.$tkey('initialisedMarginInfo'),
                groupId: `initialisedMarginInfo`,
                children: [
                  ...initialisedMarginColumn,
                  ...this.createAllInitialisedMarginInfoColumns(),
                ],
              },
            ]
          : [];
      return [
        {
          headerName: this.$t('linkText.engineInputs'),
          groupId: `mandatoryEngineInputs`,
          children: [
            ...forecastVolumeColumn,
            this.createPercentageColumn('targetMargin', {
              cellEditorParams: params => {
                if (isNaN(params.value)) return { value: params.value };
                if (isNil(params.value)) return { value: undefined };
                return { value: round(params.value, 2) };
              },
            }),
            {
              headerName: `${this.$tkey('creationCost')} (${this.$t('clientCurrencySymbol')})`,
              headerTooltip: `${this.$tkey('creationCost')} (${this.$t('clientCurrencySymbol')})`,
              colId: `mandatoryEngineInputs::creationCost`,
              field: `mandatoryEngineInputs.creationCost`,
              editable: true, // TODO: only allow editing if enabled is true
              tooltipValueGetter: this.tooltipValueGetter,
              ...getCostFormatNumericCol(
                get(this.numberFormats, [
                  this.i18nconfig.overrideNumberFormat,
                  numberFormats.costFormat,
                ])
              ),
            },
            ...(this.enableReferenceDate
              ? [
                  {
                    headerName: `${this.$tkey('referenceDateCost')} (${this.$t(
                      'clientCurrencySymbol'
                    )})`,
                    headerTooltip: `${this.$tkey('referenceDateCost')} (${this.$t(
                      'clientCurrencySymbol'
                    )})`,
                    colId: `mandatoryEngineInputs::referenceDateCost`,
                    field: `mandatoryEngineInputs.referenceDateCost`,
                    tooltipValueGetter: this.tooltipValueGetter,
                    ...getCostFormatNumericCol(
                      get(this.numberFormats, [
                        this.i18nconfig.overrideNumberFormat,
                        numberFormats.costFormat,
                      ])
                    ),
                    // editable must be set after getCostFormatNumericCol,
                    // because it uses localisedNumericCol, which sets editable: true
                    editable: false,
                  },
                ]
              : []),
            {
              headerName: `${this.$tkey('goLiveCost')} (${this.$t('clientCurrencySymbol')})`,
              headerTooltip: `${this.$tkey('goLiveCost')} (${this.$t('clientCurrencySymbol')})`,
              colId: `mandatoryEngineInputs::nonPromoNetCost`,
              field: `mandatoryEngineInputs.nonPromoNetCost`,
              tooltipValueGetter: this.tooltipValueGetter,
              ...getCostFormatNumericCol(
                get(this.numberFormats, [
                  this.i18nconfig.overrideNumberFormat,
                  numberFormats.costFormat,
                ])
              ),
            },
            this.createPercentageColumn('promoParticipationSales'),
            this.createPercentageColumn('promoDiscount'),
            this.createPercentageColumn('promoFunding'),
            {
              headerName: `${this.$tkey('intentionCost')} (${this.$t('clientCurrencySymbol')})`,
              headerTooltip: `${this.$tkey('intentionCost')} (${this.$t('clientCurrencySymbol')})`,
              colId: `mandatoryEngineInputs::intentionCost::price`,
              field: `mandatoryEngineInputs.intentionCost.price`,
              editable: !this.futureCostEditDisabled,
              tooltipValueGetter: this.tooltipValueGetter,
              ...getCostFormatNumericCol(
                get(this.numberFormats, [
                  this.i18nconfig.overrideNumberFormat,
                  numberFormats.costFormat,
                ])
              ),
            },
            ...(!this.futureCostEditDisabled
              ? [
                  {
                    headerName: this.$tkey('effectiveDate'),
                    colId: `mandatoryEngineInputs::intentionCost::effectiveDate`,
                    field: `mandatoryEngineInputs.intentionCost.effectiveDate`,
                    valueFormatter: params =>
                      this.maxDateExceeded(params.value)
                        ? '-'
                        : this.formatLocalisedDate(params.value),
                    cellEditorParams: {
                      firstDayOfWeek: this.i18nconfig.firstDayOfTheWeek,
                      minDate: () => moment('2000-01-01').format(yearMonthDayFormat),
                      locale: this.i18nconfig.fallbackLocale,
                      dateValue: date => date,
                    },
                    ...localisedDateCol,
                    tooltipValueGetter: this.tooltipValueGetter,
                    editable: !this.futureCostEditDisabled,
                  },
                  {
                    headerName: this.$tkey('expiryDate'),
                    colId: `mandatoryEngineInputs::intentionCost::expiryDate`,
                    field: `mandatoryEngineInputs.intentionCost.expiryDate`,
                    valueFormatter: params =>
                      this.maxDateExceeded(params.value)
                        ? '-'
                        : this.formatLocalisedDate(params.value),
                    cellEditorParams: {
                      firstDayOfWeek: this.i18nconfig.firstDayOfTheWeek,
                      minDate: params => {
                        return moment(
                          params.data.mandatoryEngineInputs.intentionCost.effectiveDate
                        ).format(yearMonthDayFormat);
                      },
                      locale: this.i18nconfig.fallbackLocale,
                      dateValue: date => date,
                      pickerDate: params => {
                        return this.maxDateExceeded(params.value) ? this.getTodaysDate : undefined;
                      },
                    },
                    ...localisedDateCol,
                    tooltipValueGetter: this.tooltipValueGetter,
                    editable: !this.futureCostEditDisabled,
                  },
                ]
              : []),
          ],
        },
        ...initialisedMarginInfoGroup,
      ];
    },

    hasAOExtracts() {
      return !isEmpty(get(this.selectedWorkpackage, 'aoExtracts', []));
    },
  },

  async created() {
    if (this.marginInitialisationEnabled) {
      this.fetchLastTriggeredInfo();
    }

    if (this.aoExtractsEnabled) {
      // Check if refresh static forecast volume is currently running
      const isRefreshForecastVolumeRunning = await this.isRefreshForecastVolumeRunning();
      if (!isRefreshForecastVolumeRunning) {
        this.fetchLastRefreshTriggeredInfo();
      }
    }
  },

  mounted() {
    // Listen for the refresh event
    this.$eventBus.$on('runMarginInitialisationFinished', this.refreshDataPostMarginInitialisation);
    this.$eventBus.$on('runRefreshForecastVolumeFinished', this.postRunRefreshForecastVolume);
  },

  beforeDestroy() {
    // Unsubscribe from the event to prevent memory leaks
    this.$eventBus.$off('runMarginInitialisationFinished');
    this.$eventBus.$off('runRefreshForecastVolumeFinished');
  },

  methods: {
    ...mapActions('engineInputs', ['downloadItems']),
    ...mapMutations('notifications', ['addNotification']),

    tooltipValueGetter(params) {
      return this.$refs.inputsGrid.tooltipValueGetter(params);
    },

    isMainbanner(toolStoreGroupKey) {
      // If not using zones, you only have mainbanner
      if (!this.zonesEnabled) return true;

      return toolStoreGroupKey === this.mainTsgKey;
    },

    createPercentageColumn(field, overrides = {}) {
      return {
        headerName: `${this.$tkey(`${field}`)} (%)`,
        headerTooltip: `${this.$tkey(`${field}`)} (%)`,
        colId: `mandatoryEngineInputs::${field}`,
        field: `mandatoryEngineInputs.${field}`,
        tooltipValueGetter: this.tooltipValueGetter,
        ...percentageNumericCol,
        ...overrides,
      };
    },

    /**
     * Creates a column configuration for initialisedMarginInfo fields
     * @param {string} field - The field name within initialisedMarginInfo
     * @param {Object} overrides - Additional configuration to override defaults
     * @returns {Object} Column configuration object
     */
    createInitialisedMarginInfoColumn(field, overrides = {}) {
      const currencySymbol =
        field === 'price' || field === 'cost' ? ` (${this.$t('clientCurrencySymbol')})` : '';

      const defaultConfig = {
        headerName: `${this.$tkey(`initialisedMarginInfoCols.${field}`)}${currencySymbol}`,
        headerTooltip: `${this.$tkey(`initialisedMarginInfoCols.${field}`)}${currencySymbol}`,
        colId: `initialisedMarginInfo::${field}`,
        field: `initialisedMarginInfo.${field}`,
        tooltipValueGetter: this.tooltipValueGetter,
        ...localisedNumericCol,
      };

      return {
        ...defaultConfig,
        ...overrides,
        editable: false,
      };
    },

    /**
     * Creates competitor price columns for initialisedMarginInfo
     * @returns {Array} Array of competitor price column configuration objects
     */
    createInitialisedMarginInfoCompetitorColumns() {
      return flatMap(this.feedCompetitorNames, competitor => {
        return [
          {
            headerName: `${competitor} ${this.$t('competitorMatches.editor.price')} (${this.$t(
              'clientCurrencySymbol'
            )})`,
            headerTooltip: `${competitor} ${this.$t('competitorMatches.editor.price')}`,
            colId: `initialisedMarginInfo::competitors::${competitor}::competitorPrice`,
            field: `initialisedMarginInfo.competitors.${competitor}.competitorPrice`,
            editable: false,
            tooltipValueGetter: this.tooltipValueGetter,
          },
        ];
      });
    },

    /**
     * Creates all column configurations for initialisedMarginInfo fields
     * @param {Object} fieldOverrides - Override configurations for specific fields
     * @returns {Array} Array of column configuration objects
     */
    createAllInitialisedMarginInfoColumns() {
      const standardColumns = [
        'price',
        'cost',
        'vat',
        'competitorReferencePrice',
        'exactMatchCompetitor',
      ];
      const standardOverrides = {
        cost: {
          ...getCostFormatNumericCol(
            get(this.numberFormats, [
              this.i18nconfig.overrideNumberFormat,
              numberFormats.costFormat,
            ])
          ),
        },
        vat: {
          ...percentageNumericCol,
          valueFormatter: params => {
            if (isNil(params.value) || isNaN(params.value)) {
              return params.value;
            }
            return (params.value * 100).toFixed(2);
          },
        },
        exactMatchCompetitor: {
          cellEditor: 'agTextCellEditor',
          cellRenderer: undefined,
          valueFormatter: undefined,
          valueParser: undefined,
        },
      };
      const competitorColumns = this.createInitialisedMarginInfoCompetitorColumns();

      return [
        ...standardColumns.map(field => {
          return this.createInitialisedMarginInfoColumn(field, {
            ...standardOverrides[field],
          });
        }),
        ...competitorColumns,
      ];
    },

    getFormatters() {
      const clientDateFormat = this.dateFormats.clientDateFormatForMoment;
      const columnFormatters = this.getColumnFormatters(this.headers);
      each(this.dateColumnTranslations, column => {
        columnFormatters[column] = {
          type: DataTypes.date,
          format: clientDateFormat,
        };
      });
      return columnFormatters;
    },

    downloadEngineInputs() {
      this.downloadItems({
        where: this.retailAttributesFilter,
        translationMap: this.exportTranslationMap,
        pick: this.getColumnExportPickOptions(this.toolStoreGroupColumn),
        columnFormatters: this.getFormatters(),
      });
    },

    async saveChanges({ updates, mainTsgKey }) {
      const { data: results } = await axios.post(
        `/api/inputs/workpackage/${this.selectedWorkpackage._id}/engine`,
        { updates, mainTsgKey }
      );
      return results;
    },

    /* ----------------- Run Margin Initialisation Methods Start ---------------- */
    async refreshDataPostMarginInitialisation({ payload: workpackageId }) {
      const selectedWorkpackageId = this.selectedWorkpackage._id || workpackageId;
      // Ensure that response matches current workpackageId
      if (workpackageId !== selectedWorkpackageId) {
        return;
      }

      // Reload grid data after refresh
      await Promise.all([
        this.$refs.inputsGrid.fetchGridData(),
        await this.fetchLastTriggeredInfo(),
      ]);
    },

    async fetchLastTriggeredInfo() {
      try {
        const workpackageId = this.selectedWorkpackage._id;

        this.isLastTriggeredLoading = true;
        // Build query parameters
        const params = {
          where: {
            fullEventName: Events.engineInputs.initialisationRun,
            $and: [{ entityIds: { $in: [workpackageId] } }],
          },
          sortBy: 'date',
          sortDirection: descending,
          limit: 1,
        };

        // Make API request
        const { data: auditLogs } = await axios.get('/api/audit-log', { params });

        this.lastTriggeredInfo = get(auditLogs, 0, {});
      } catch (error) {
        console.error('Error fetching last triggered info:', error);
        this.lastTriggeredInfo = {};
      } finally {
        this.isLastTriggeredLoading = false;
      }
    },

    async fetchLastRefreshTriggeredInfo() {
      try {
        const workpackageId = this.selectedWorkpackage._id;

        this.isLastRefreshTriggeredLoading = true;
        // Build query parameters
        const params = {
          where: {
            fullEventName: Events.engineInputs.refreshForecastVolume,
            $and: [{ entityIds: { $in: [workpackageId] } }],
          },
          sortBy: 'date',
          sortDirection: descending,
          limit: 1,
        };

        // Make API request
        const { data: auditLogs } = await axios.get('/api/audit-log', { params });

        this.lastRefreshTriggeredInfo = get(auditLogs, 0, {});
      } catch (error) {
        console.error('Error fetching last refresh triggered info:', error);
        this.lastRefreshTriggeredInfo = {};
      } finally {
        this.isLastRefreshTriggeredLoading = false;
      }
    },
    /* ----------------- Run Margin Initialisation Methods End ----------------  */

    /* ----------------- Run Refresh Forecast Volume Methods Start ---------------- */
    confirmRefreshForecast() {
      this.isRefreshForecastDialogOpen = false;
      this.runRefreshForecastVolume({
        workpackageId: this.selectedWorkpackage._id,
      });
    },

    async runRefreshForecastVolume() {
      this.runningRefreshForecastVolume = true;
      const [err] = await to(
        axios.post(
          `/api/inputs/workpackage/${this.selectedWorkpackage._id}/runRefreshForecastVolume`,
          {}
        )
      );
      if (err) {
        this.runningRefreshForecastVolume = false;
      }
    },

    async postRunRefreshForecastVolume({ payload: workpackageId }) {
      const selectedWorkpackageId = this.selectedWorkpackage._id || workpackageId;
      // Ensure that response matches current workpackageId
      if (workpackageId !== selectedWorkpackageId) {
        return;
      }

      this.runningRefreshForecastVolume = false;

      // Reload grid data after refresh
      await Promise.all([
        this.$refs.inputsGrid.fetchGridData(),
        await this.fetchLastRefreshTriggeredInfo(),
      ]);
    },

    async isRefreshForecastVolumeRunning() {
      const [err, res] = await to(
        axios.get(
          `/api/inputs/workpackage/${this.selectedWorkpackage._id}/runRefreshForecastVolume/status`
        )
      );
      if (err) {
        this.runningRefreshForecastVolume = false;
        throw new Error(err.message);
      }
      const { data: isRefreshForecastVolumeRunning } = res;
      this.runningRefreshForecastVolume = isRefreshForecastVolumeRunning;
      return isRefreshForecastVolumeRunning;
    },
    /* ----------------- Run Refresh Forecast Volume Methods End ----------------  */
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';

.main-header th {
  position: relative;
}

#buttons-bar {
  max-width: 20rem;
}

.v-list-item__title {
  font-size: 1.5rem;
}

.v-list-item {
  min-height: 35px;
}

.no-data-slot {
  padding-left: 2.2rem;
}

.input-screen-page-wrapper .table-cell::v-deep .tooltipped-truncated-field {
  position: unset;
}

.date-attribute-cell {
  position: relative;
}

.invalid-blocking {
  border-bottom: 0.2rem solid #c13939 !important;
}

.pale {
  opacity: 0.7;
}

.workflow-controls {
  .workflow-buttons {
    align-items: center;
  }

  .workflow-info-row {
    margin-top: 4px;
  }
}
</style>
